import type { ReactElement, ReactNode } from 'react';
import { FlatList, RefreshControl, useColorScheme, type FlatListProps } from 'react-native';
import { View, YStack } from 'tamagui';

import { Text } from '@/components/text';
import { AlertCircle as AlertIcon } from 'lucide-react-native';
import { EmptyState } from '@/components/empty-state';
import { PageEnd } from '@/components/page-end';
import { iconSize, iconStroke } from '@/theme/icon';
import { LoadingIndicator } from '@/components/loading-indicator';
import { TextLink } from '@/components/text-link';
import type { PaginatedResource } from '@/hooks/use-paginated-resource';
import { darkColors, lightColors } from '@/theme/tokens.generated';

// The thin presentational half of the list-screen machine
// (hooks/use-paginated-resource.ts is the state half): pending,
// error-without-data, the stale-refresh notice, the shared
// fetch-next-page footer, and the RefreshControl with its resolved
// native colors, written once instead of re-derived per screen. The
// caller owns its difference -- the items' render and list behaviour
// props (inverted, onScroll, key, contentContainerStyle...) pass
// through untouched, plus the empty state and, when the default does
// not fit, the pending/error nodes.

// Native semantic colors, not Tamagui tokens -- RefreshControl is a
// real native view (UIRefreshControl / Android SwipeRefreshLayout) that
// takes resolved color strings, the same reasoning as every screen's
// pre-extraction RefreshControl setup.

// Stale-refresh copy, written once. The screens' six inline copies of
// this block all read exactly this: centered destructive micro-text.
// The notice variant wraps it in the padded YStack the FlatList screens
// render above their list; the bare-text variant is for callers already
// inside a gap-managed scroll container (reveal-requests' ScrollView),
// where the wrapper would change the layout.
export function StaleRefreshText() {
  return (
    <Text color="$destructive" fontSize="$meta" lineHeight="$meta" textAlign="center">
      Couldn&apos;t refresh. Showing what&apos;s already loaded.
    </Text>
  );
}

export function StaleRefreshNotice({ pt, pb }: { pt?: string; pb?: string }) {
  return (
    <YStack pt={pt} pb={pb}>
      <StaleRefreshText />
    </YStack>
  );
}

// The one fetch-next-page footer that replaced ThreadsListFooter,
// MatchesListFooter, and OlderMessagesFooter (messages passes its own
// labels: "earlier messages" instead of "more"). A later-page failure
// states inline with a retry and never clears the already-rendered
// pages -- those come straight from the query's cached pages, untouched
// by a failed fetchNextPage. Kept separate from the screen's own
// first-load error state: a later-page failure must never swap out the
// whole list.
export function ResourceListFooter({
  isFetchingNextPage,
  isFetchNextPageError,
  onRetry,
  errorLabel = "Couldn't load more",
  loadingLabel = 'Loading more',
}: {
  isFetchingNextPage: boolean;
  isFetchNextPageError: boolean;
  onRetry: () => void;
  errorLabel?: string;
  loadingLabel?: string;
}) {
  if (isFetchNextPageError) {
    return (
      <YStack alignItems="center" pt="$2" pb="$4" gap="$2">
        <Text color="$destructive" fontSize="$meta" lineHeight="$meta" textAlign="center">
          {errorLabel}
        </Text>
        {/* px/py pad the tap target to >=24x24 (WCAG 2.5.8) -- the text
            itself at fontSize="$meta" lineHeight="$meta" is well under that on its own. */}
        <TextLink fontSize="$meta" lineHeight="$meta" fontWeight="$semibold" px="$2" py="$1" onPress={onRetry}>
          Try again
        </TextLink>
      </YStack>
    );
  }
  if (isFetchingNextPage) {
    return (
      <View pt="$2" pb="$4" alignItems="center">
        <LoadingIndicator label={loadingLabel} />
      </View>
    );
  }
  return null;
}

export interface ResourceListViewProps<TItem> {
  resource: PaginatedResource<TItem>;
  // Rendered as ListEmptyComponent, inside the list, so the empty state
  // keeps a scrollable ancestor and pull-to-refresh still reaches it
  // (each screen's own pre-extraction fix for exactly that).
  emptyNode: ReactElement;
  // The first-load node, for screens whose skeleton previews the real
  // layout (threads' card skeletons, messages' bubble skeletons).
  // Default: a centered LoadingIndicator.
  pendingNode?: ReactNode;
  // The error-without-data node, for screens whose first-load failure is
  // not a plain retry (threads' needsProfile "create a profile" state).
  // Default: EmptyState + Try again -> refetch.
  errorNode?: ReactNode;
  // Title for the default error node above.
  errorTitle: string;
  // Extra content rendered below the items but above the fetch-more
  // footer -- the sparse-list explainer card (decision 4, issue #563).
  // The screen decides whether/what to show; the view only owns the
  // slot, so pagination and the refresh control stay untouched.
  footer?: ReactElement;
  // Quiet closing line once the last page is on screen.
  endNote?: string;
  // React `key` for the underlying FlatList, for screens that remount
  // it on a filter change (threads' category switch) -- React's `key`
  // is not part of FlatListProps, so it gets its own slot.
  listKey?: string;
  // Everything else the screen needs off the FlatList -- inverted,
  // onScroll, ref, key, style, contentContainerStyle, renderItem,
  // keyExtractor -- passed through untouched.
  // The items' render -- the screen's one real difference.
  renderItem: FlatListProps<TItem>['renderItem'];
  listProps?: Omit<
    FlatListProps<TItem>,
    | 'data'
    | 'renderItem'
    | 'ListEmptyComponent'
    | 'ListFooterComponent'
    | 'refreshControl'
    | 'onEndReached'
    | 'onEndReachedThreshold'
  >;
}

export function ResourceListView<TItem>({
  resource,
  emptyNode,
  pendingNode,
  errorNode,
  errorTitle,
  footer,
  endNote,
  listKey,
  renderItem,
  listProps,
}: ResourceListViewProps<TItem>) {
  const colorScheme = useColorScheme();
  const colors = colorScheme === 'dark' ? darkColors : lightColors;

  if (resource.state === 'pending') {
    return (
      pendingNode ?? (
        <View flex={1} alignItems="center" justifyContent="center">
          <LoadingIndicator />
        </View>
      )
    );
  }

  if (resource.state === 'error') {
    return (
      errorNode ?? (
        <EmptyState
          icon={<AlertIcon size={iconSize.dock} strokeWidth={iconStroke} color={colors.actionPrimary} />}
          title={errorTitle}
          description="Check your connection and try again."
          action={{ label: 'Try again', onPress: () => void resource.refetch() }}
        />
      )
    );
  }

  return (
    <>
      {/* Background refetch failed (e.g. a failed pull-to-refresh) but
          real data is still on screen -- says so rather than silently
          keeping stale content with no signal at all ("feedback may not
          outrun the truth"). Stale outranks empty on purpose: the
          notice renders above a list that still owns its empty state. */}
      {resource.state === 'stale' && <StaleRefreshNotice pb="$2" />}
      <FlatList
        key={listKey}
        data={resource.items}
        renderItem={renderItem}
        ListEmptyComponent={emptyNode}
        // The double-fire guard lives in the resource hook
        // (hasNextPage + !isFetchingNextPage).
        onEndReached={resource.fetchNextPage}
        onEndReachedThreshold={0.5}
        ListFooterComponent={
          <>
            {footer}
            <ResourceListFooter
              isFetchingNextPage={resource.isFetchingNextPage}
              isFetchNextPageError={resource.isFetchNextPageError}
              onRetry={resource.fetchNextPage}
            />
            {endNote && resource.items.length > 0 && !resource.hasNextPage && <PageEnd>{endNote}</PageEnd>}
          </>
        }
        refreshControl={
          <RefreshControl
            refreshing={resource.isRefetching}
            onRefresh={resource.refresh}
            tintColor={colors.actionPrimary}
            colors={[colors.actionPrimary]}
            progressBackgroundColor={colors.surfacePage}
          />
        }
        {...listProps}
      />
    </>
  );
}
